import React, { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { useSetRecoilState } from 'recoil';
import { POPUP_INDEX } from '../../../styles/constants/priority';
import Portal from '../Portal';
import CloseButton from '../button/CloseButton';
import { boxShadow, WHITE } from '../../../styles/constants/color';
import PrevButton from '../button/PrevButton';
import { MODAL_PADDING_HOR, MODAL_PADDING_VER } from '../../../styles/constants/size';
import useSwitching from '../../../hooks/common/useSwitching';
import { sustainableBgState } from '../../../data/estimate/common/modal/atom';
import { blockBack } from '../../../lib/common';

const Background = styled.div<{
  type: string;
  active: boolean;
  zIndex?: number;
  needTransition: boolean;
  isBgLess: boolean;
}>`
  top: 0;
  left: 0;
  position: fixed;
  width: 100%;
  height: 100%;
  background-color: ${props => (props.isBgLess ? 'transparent' : 'rgba(0, 0, 0, 0.7)')};
  z-index: ${props => props.zIndex && props.zIndex};
  opacity: ${props => (props.active ? 1 : 0)};
  transition: opacity ${props => (props.type === 'modal' ? '0.15s ease-in-out' : '0.07s linear')};
  overflow: hidden;
`;

const Base = styled.div<{ zIndex: number }>`
  top: 50%;
  left: 50%;
  position: absolute;
  transform: translate(-50%, -50%);
  width: auto;
  height: auto;
  max-height: 98vh;
  z-index: ${props => props.zIndex + 1};
  background-color: ${WHITE};
  border-radius: 8px;
  box-shadow: ${boxShadow};
  padding: ${`${MODAL_PADDING_VER} ${MODAL_PADDING_HOR} 20px`};

  @media (max-width: 500px) {
    padding: 25px 25px 15px;
  }
`;

const ButtonWrapper = styled.div<{ position: string }>`
  position: absolute;

  ${props =>
    props.position === 'left' &&
    css`
      top: 2px;
      left: 5px;
    `}

  ${props =>
    props.position === 'right' &&
    css`
      top: 3px;
      right: 3px;
    `} /* @media (max-width: 500px) {
    & svg {
      width: 30px;
      height: 30px;
    }
  } */
`;

interface Props {
  type?: string;
  active: boolean;
  children: React.ReactNode;
  targetId?: string;
  zIndex?: number;
  subTrigger?: boolean;
  isBgLess?: boolean;
  blockPrevShortCut?: boolean;
  blockCloseShortCut?: boolean;
  prevEvent?: () => void;
  closeEvent: () => void;
}

function ModalBase({
  type = 'modal',
  active,
  children,
  targetId = 'portal',
  zIndex = POPUP_INDEX,
  subTrigger,
  isBgLess = false,
  blockPrevShortCut = false,
  blockCloseShortCut = false,
  prevEvent,
  closeEvent,
}: Props): JSX.Element | null {
  const backgroundRef = useRef<HTMLDivElement>(null);
  const baseRef = useRef<HTMLDivElement>(null);

  const setSustainableBgSelector = useSetRecoilState<boolean>(sustainableBgState);

  const [trigger, setTrigger] = useState<boolean>(false); // portal을 포함한 그 하위 컴포넌트들의 렌더링 여부
  const [transitionTrigger, switchingTransitionTrigger]: [boolean, (param?: boolean) => void] =
    useSwitching(); // portal을 포함한 그 하위 컴포넌트들이 렌더링 후에 모달의 transition을 주기 위한 변수
  const closeModal = (): void => {
    closeEvent(); // onTransitionEnd 함수에서 closeEvent() 호출 부분을 대신해서 사용 (오류로 인해)
    document.body.style.overflow = 'auto';
    switchingTransitionTrigger(false);
  };
  /**
   *  현재 모달을 transition 효과를 주면서 닫기 위한 함수
   *  */
  const onTransitionEnd = (): void => {
    if (transitionTrigger) {
      document.body.style.overflow = 'hidden';
    } else {
      // closeEvent(); ==> 오류로 현재 사용 보류됨.
      document.body.style.overflow = 'auto';
    }
  };

  const onKeyDown = (e: React.KeyboardEvent): void => {
    const key: string = e.key;

    if (key === 'Escape') {
      // ESC 누를 시 모달 닫기

      // trigger && transitionTrigger 이유: ESC 연타시 닫히는 모달이 다시 열려 깜빡이게 하는 현상을 방지하기 위해서
      !blockCloseShortCut && trigger && transitionTrigger && closeModal();
      !blockCloseShortCut && isBgLess && setSustainableBgSelector(false);
    }

    !blockPrevShortCut && key === 'Backspace' && prevEvent && prevEvent(); // Backspace 누를 시 이전 모달로 뒤로가기
  };

  useEffect(() => {
    blockBack('modal', () => {
      !blockCloseShortCut && isBgLess && setSustainableBgSelector(false);
      closeEvent();
    });

    return () => {
      // 이 모달이 닫힐 때 스크롤 lock을 풀어준다.
      document.body.style.overflow = 'auto';
    };
  }, []);

  useEffect(() => {
    active !== trigger && setTrigger(active);
    if (active) {
      document.body.style.overflow = 'hidden';
    }
  }, [active]);

  useEffect(() => {
    // 모달이 열릴 때 프로세스
    if (trigger) {
      setTimeout(() => {
        // trigger !== transitionTrigger && switchingTransitionTrigger();
        switchingTransitionTrigger(true);
      }, 100);

      // blockBack('modal', () => {
      //   !blockCloseShortCut && isBgLess && setSustainableBgSelector(false);
      //   closeEvent();
      // });
      document.body.style.overflow = 'hidden';
    }
  }, [trigger]);

  useEffect(() => {
    // 백그라운드에 focus를 주어서 팝업이 뜬후 키보드로 닫기 혹은 뒤로가기를 할 수 있도록 해준다.
    if (transitionTrigger) {
      backgroundRef.current?.focus();
    }
  }, [transitionTrigger]);

  useEffect(() => {
    // children 에서 아이템 선택해서 팝업을 닫아준다.
    if (subTrigger === false) {
      switchingTransitionTrigger(false);
      closeEvent(); // onTransitionEnd 함수에서 closeEvent() 호출 부분을 대신해서 사용 (오류로 인해)
      document.body.style.overflow = 'auto';
    }
  }, [subTrigger]);

  return (
    <>
      {trigger ? (
        <Portal id={targetId}>
          <Background
            ref={backgroundRef}
            type={type}
            active={transitionTrigger}
            zIndex={zIndex}
            needTransition
            onKeyDown={onKeyDown}
            // onClick={() => {
            //   // trigger && transitionTrigger 이유: ESC 연타시 닫히는 모달이 다시 열려 깜빡이게 하는 현상을 방지하기 위해서
            //   trigger && transitionTrigger && closeModal();
            //   isBgLess && setSustainableBgSelector(false);
            // }}
            onTransitionEnd={onTransitionEnd}
            tabIndex={0}
            isBgLess={isBgLess}
          >
            <Base
              zIndex={zIndex}
              ref={baseRef}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
              }}
            >
              {prevEvent && (
                <ButtonWrapper position='left'>
                  <PrevButton onClick={prevEvent} />
                </ButtonWrapper>
              )}
              <ButtonWrapper position='right'>
                <CloseButton
                  onClick={() => {
                    closeModal();
                    isBgLess && setSustainableBgSelector(false);
                  }}
                />
              </ButtonWrapper>
              {children}
            </Base>
          </Background>
        </Portal>
      ) : null}
    </>
  );
}

/**
 *  공통 모달 컴포넌트 모달의 transition 효과를 주기 위해 trigger, transitionTrigger의 state를 사용한다.
 * trigger로 모달의 렌더링을 시켜주고, Background 컴포넌트가 needTransition을 기반으로 opactiy가 0인 상태에서 렌더링이 먼저 되는데 그 이후에 transitionTrigger로
 * opactiy를 1로 증가시켜준다. 반대로 닫을 경우 닫기 버튼으로 먼저 transitionTrigger를 false로 만들어주어서 Background의 opcaity를 1에서 0으로 변경시켜준뒤
 * onTransitionEnd를 이용해 모달 컴포넌트를 닫는 closeEvent를 실행시켜 active를 false로 변경시켜주면 trigger상태가 false로 변경되어 렌더링을 시켜주지 않는다.
 * @param {string} type 모달 타입 transition의 속도를 판별한다 (modal or alert, default: modal)
 * @param {boolean} active 모달 활성화 여부
 * @param {ChildNode} children 공통 모달 바디에 들어갈 컴포넌트
 * @param {string} targetId portal 열어줄 상위 엘리먼트의 id (default: 'portal')
 * @param {number} zIndex 모달 z-index (defalut: 4)
 * @param {boolean} subTrigger children에서 click으로 팝업이 닫혀야 될 때 transitionEnd를 주기 위한 파라미터
 * @param {boolean} isBgLess 검은색 배경이 필요한지에 대한 여부 (default: false)
 * @param {boolean} blockPrevShortCut 뒤로가기 단축키 막기 여부 (defalut: false)
 * @param {boolean} blockCloseShortCut 닫기 단축키 막기 여부 (defalut: false)
 * @param {function} prevEvent 모달 뒤로가기 이벤트 함수
 * @param {function} closeEvent 모달 닫기 이벤트 함수
 *  */
export default React.memo(ModalBase);
